/*
Copyright 2026 AB Civil Consulting
Improved React hook for meeting recording in Element Call.
- Better state management
- Recording banner notification to all participants
- Error recovery
- Duration tracking
- Uploads recordings to Matrix room (no Element Desktop required)
*/

import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Room, RoomEvent, DataPacket_Kind } from "livekit-client";
import { RecordingService, RecordingConfig } from "./RecordingService";

export type RecordingState =
    | "idle"
    | "starting"
    | "recording"
    | "stopping"
    | "uploading"
    | "saved"
    | "error";

export interface RecordingStatus {
    state: RecordingState;
    duration: number;
    participantCount: number;
    error: string | null;
    uploadProgress?: number;
    matrixEventId?: string;
}

export interface UseRecordingOptions {
    config?: RecordingConfig;
    autoNotifyParticipants?: boolean;
}

export interface UseRecordingResult {
    status: RecordingStatus;
    isRecording: boolean;
    isSupported: boolean;
    startRecording: () => Promise<boolean>;
    stopRecording: () => Promise<void>;
    cancelRecording: () => void;
}

const RECORDING_DATA_TOPIC = "recording-status";

function isRecordingSupported(): boolean {
    if (typeof MediaRecorder === "undefined") {
        console.log("[useRecording] MediaRecorder not supported");
        return false;
    }
    return true;
}

async function broadcastRecordingStatus(room: Room, isRecording: boolean, recordedBy: string): Promise<void> {
    try {
        const message = JSON.stringify({
            topic: RECORDING_DATA_TOPIC,
            isRecording,
            recordedBy,
            timestamp: Date.now(),
        });
        const encoder = new TextEncoder();
        const data = encoder.encode(message);
        await room.localParticipant.publishData(data, {
            reliable: true,
            topic: RECORDING_DATA_TOPIC,
        });
        console.log("[useRecording] Broadcast recording status:", isRecording);
    } catch (e) {
        console.error("[useRecording] Failed to broadcast recording status:", e);
    }
}

function base64ToBlob(base64: string, mimeType: string): Blob {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
}

function formatDurationForFilename(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes > 0) {
        return minutes + "m" + seconds + "s";
    }
    return seconds + "s";
}

export function useRecording(
    livekitRoom: Room | null,
    matrixClient: any,
    matrixRoomId: string,
    roomName: string,
    options?: UseRecordingOptions
): UseRecordingResult {
    const [status, setStatus] = useState<RecordingStatus>({
        state: "idle",
        duration: 0,
        participantCount: 0,
        error: null,
    });

    const recordingServiceRef = useRef<RecordingService | null>(null);
    const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const startTimeRef = useRef<number>(0);
    const chunksRef = useRef<Blob[]>([]);

    const isSupported = useMemo(() => isRecordingSupported(), []);
    const isRecording = status.state === "recording";

    useEffect(() => {
        if (!livekitRoom) return;
        const handleDataReceived = (
            payload: Uint8Array,
            participant: any,
            kind: DataPacket_Kind,
            topic?: string
        ) => {
            if (topic !== RECORDING_DATA_TOPIC) return;
            try {
                const decoder = new TextDecoder();
                const message = JSON.parse(decoder.decode(payload));
                console.log("[useRecording] Received recording status from:", participant?.identity, message);
            } catch (e) {
                console.error("[useRecording] Failed to parse recording status:", e);
            }
        };
        livekitRoom.on(RoomEvent.DataReceived, handleDataReceived);
        return () => {
            livekitRoom.off(RoomEvent.DataReceived, handleDataReceived);
        };
    }, [livekitRoom]);

    useEffect(() => {
        return () => {
            if (durationIntervalRef.current) {
                clearInterval(durationIntervalRef.current);
                durationIntervalRef.current = null;
            }
            if (recordingServiceRef.current) {
                recordingServiceRef.current.dispose();
                recordingServiceRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!livekitRoom) return;
        const updateCount = () => {
            setStatus(prev => ({
                ...prev,
                participantCount: 1 + livekitRoom.remoteParticipants.size,
            }));
        };
        updateCount();
        livekitRoom.on(RoomEvent.ParticipantConnected, updateCount);
        livekitRoom.on(RoomEvent.ParticipantDisconnected, updateCount);
        return () => {
            livekitRoom.off(RoomEvent.ParticipantConnected, updateCount);
            livekitRoom.off(RoomEvent.ParticipantDisconnected, updateCount);
        };
    }, [livekitRoom]);

    const startRecording = useCallback(async (): Promise<boolean> => {
        console.log("[useRecording] ========== START RECORDING ==========");
        console.log("[useRecording] LiveKit room:", livekitRoom?.name, "state:", livekitRoom?.state);
        console.log("[useRecording] Matrix Room ID:", matrixRoomId);

        if (!isSupported) {
            console.log("[useRecording] Recording not supported");
            setStatus(prev => ({ ...prev, state: "error", error: "MediaRecorder not supported in this browser" }));
            return false;
        }
        if (status.state === "recording" || status.state === "starting") {
            console.log("[useRecording] Already recording or starting");
            return false;
        }
        if (!livekitRoom || livekitRoom.state !== "connected") {
            console.log("[useRecording] LiveKit room not connected");
            setStatus(prev => ({ ...prev, state: "error", error: "Not connected to call" }));
            return false;
        }
        if (!matrixClient) {
            console.log("[useRecording] Matrix client not available");
            setStatus(prev => ({ ...prev, state: "error", error: "Matrix client not available" }));
            return false;
        }

        setStatus(prev => ({ ...prev, state: "starting", error: null }));
        chunksRef.current = [];

        try {
            console.log("[useRecording] Creating RecordingService...");
            recordingServiceRef.current = new RecordingService(livekitRoom, matrixRoomId, roomName, options?.config);

            recordingServiceRef.current.onChunk = (base64Chunk: string) => {
                console.log("[useRecording] Received chunk, size:", base64Chunk.length);
                const blob = base64ToBlob(base64Chunk, "video/webm");
                chunksRef.current.push(blob);
            };

            recordingServiceRef.current.onError = (error: Error) => {
                console.error("[useRecording] Recording error:", error);
                setStatus(prev => ({ ...prev, state: "error", error: error.message }));
            };

            recordingServiceRef.current.onParticipantChange = (count: number) => {
                setStatus(prev => ({ ...prev, participantCount: count }));
            };

            console.log("[useRecording] Starting recording service...");
            const success = await recordingServiceRef.current.start();

            if (success) {
                startTimeRef.current = Date.now();
                setStatus(prev => ({
                    ...prev,
                    state: "recording",
                    duration: 0,
                    participantCount: 1 + livekitRoom.remoteParticipants.size,
                }));
                durationIntervalRef.current = setInterval(() => {
                    const duration = Date.now() - startTimeRef.current;
                    setStatus(prev => ({ ...prev, duration }));
                }, 1000);
                if (options?.autoNotifyParticipants !== false) {
                    const myIdentity = livekitRoom.localParticipant.identity;
                    await broadcastRecordingStatus(livekitRoom, true, myIdentity);
                }
                console.log("[useRecording] Recording started successfully!");
                return true;
            } else {
                throw new Error("Failed to start recording service");
            }
        } catch (err) {
            console.error("[useRecording] Start failed:", err);
            if (recordingServiceRef.current) {
                recordingServiceRef.current.dispose();
                recordingServiceRef.current = null;
            }
            chunksRef.current = [];
            setStatus(prev => ({ ...prev, state: "error", error: (err as Error).message || "Failed to start recording" }));
            return false;
        }
    }, [isSupported, status.state, livekitRoom, matrixRoomId, roomName, matrixClient, options]);

    const stopRecording = useCallback(async (): Promise<void> => {
        console.log("[useRecording] ========== STOP RECORDING ==========");
        if (durationIntervalRef.current) {
            clearInterval(durationIntervalRef.current);
            durationIntervalRef.current = null;
        }
        if (!recordingServiceRef.current) {
            console.log("[useRecording] No recording service to stop");
            return;
        }
        const finalDuration = Date.now() - startTimeRef.current;
        setStatus(prev => ({ ...prev, state: "stopping" }));

        try {
            console.log("[useRecording] Stopping recording service...");
            await recordingServiceRef.current.stopAndFinalize();

            if (livekitRoom && options?.autoNotifyParticipants !== false) {
                const myIdentity = livekitRoom.localParticipant.identity;
                await broadcastRecordingStatus(livekitRoom, false, myIdentity);
            }

            console.log("[useRecording] Recording stopped, chunks collected:", chunksRef.current.length);

            if (chunksRef.current.length > 0 && matrixClient) {
                setStatus(prev => ({ ...prev, state: "uploading", uploadProgress: 0 }));

                const finalBlob = new Blob(chunksRef.current, { type: "video/webm" });
                const fileSizeMB = (finalBlob.size / (1024 * 1024)).toFixed(2);
                console.log("[useRecording] Final recording size:", fileSizeMB, "MB");

                const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
                const durationStr = formatDurationForFilename(finalDuration);
                const safeRoomName = roomName.replace(/[^a-zA-Z0-9]/g, "_").slice(0, 30);
                const filename = "recording_" + safeRoomName + "_" + timestamp + "_" + durationStr + ".webm";
                console.log("[useRecording] Uploading to Matrix as:", filename);

                try {
                    const uploadResponse = await matrixClient.uploadContent(finalBlob, {
                        name: filename,
                        type: "video/webm",
                        progressHandler: (progress: { loaded: number; total: number }) => {
                            const percent = Math.round((progress.loaded / progress.total) * 100);
                            console.log("[useRecording] Upload progress:", percent, "%");
                            setStatus(prev => ({ ...prev, uploadProgress: percent }));
                        },
                    });

                    const mxcUrl = uploadResponse.content_uri;
                    console.log("[useRecording] Upload complete, MXC URL:", mxcUrl);

                    const eventContent = {
                        msgtype: "m.video",
                        body: filename,
                        filename: filename,
                        url: mxcUrl,
                        info: {
                            mimetype: "video/webm",
                            size: finalBlob.size,
                            duration: finalDuration,
                        },
                    };

                    const sendResponse = await matrixClient.sendMessage(matrixRoomId, eventContent);
                    console.log("[useRecording] Message sent, event ID:", sendResponse.event_id);

                    setStatus(prev => ({ ...prev, state: "saved", matrixEventId: sendResponse.event_id }));

                    setTimeout(() => {
                        setStatus(prev => prev.state === "saved" ? {
                            ...prev,
                            state: "idle",
                            duration: 0,
                            uploadProgress: undefined,
                            matrixEventId: undefined,
                        } : prev);
                    }, 5000);

                } catch (uploadErr) {
                    console.error("[useRecording] Upload failed:", uploadErr);
                    setStatus(prev => ({
                        ...prev,
                        state: "error",
                        error: "Failed to upload recording to Matrix: " + (uploadErr as Error).message,
                    }));
                }
            } else {
                console.log("[useRecording] No chunks to upload or no Matrix client");
                setStatus(prev => ({ ...prev, state: "idle", duration: 0 }));
            }
            chunksRef.current = [];
        } catch (err) {
            console.error("[useRecording] Stop failed:", err);
            setStatus(prev => ({ ...prev, state: "error", error: (err as Error).message || "Failed to stop recording" }));
        } finally {
            recordingServiceRef.current = null;
        }
    }, [livekitRoom, matrixClient, matrixRoomId, roomName, options]);

    const cancelRecording = useCallback((): void => {
        console.log("[useRecording] ========== CANCEL RECORDING ==========");
        if (durationIntervalRef.current) {
            clearInterval(durationIntervalRef.current);
            durationIntervalRef.current = null;
        }
        if (recordingServiceRef.current) {
            recordingServiceRef.current.dispose();
            recordingServiceRef.current = null;
        }
        chunksRef.current = [];
        if (livekitRoom && options?.autoNotifyParticipants !== false) {
            const myIdentity = livekitRoom.localParticipant.identity;
            broadcastRecordingStatus(livekitRoom, false, myIdentity).catch(() => {});
        }
        setStatus({
            state: "idle",
            duration: 0,
            participantCount: livekitRoom ? 1 + livekitRoom.remoteParticipants.size : 0,
            error: null,
        });
    }, [livekitRoom, options]);

    return {
        status,
        isRecording,
        isSupported,
        startRecording,
        stopRecording,
        cancelRecording,
    };
}

export default useRecording;
